import { useId, useRef } from "react";
import { useModalAccessibility } from "@/lib/useModalAccessibility";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

// =============================================================================
// Dialog : modal centré (vs Drawer latéral)
//
// Cas d'usage : confirmation, saisie courte, choix simple.
// Pour les formulaires longs, préférer le Drawer.
// =============================================================================

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
  /** Désactive la fermeture par backdrop ou ESC (utile pendant une mutation). */
  blocking?: boolean;
  /**
   * Niveau de stack du dialog quand il est imbriqué dans un autre.
   *   - 1 (défaut) : z-50, niveau standard
   *   - 2          : z-[60], pour qu'un dialog enfant passe au-dessus de son parent
   *   - 3          : z-[70], pour un 3ème niveau d'empilement (rare)
   */
  level?: 1 | 2 | 3;
}

const SIZE_CLASSES: Record<NonNullable<DialogProps["size"]>, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
  "2xl": "sm:max-w-3xl",
  "3xl": "sm:max-w-5xl",
};

// Classes statiques (nécessaires pour Tailwind JIT)
const LEVEL_CLASSES: Record<NonNullable<DialogProps["level"]>, string> = {
  1: "z-50",
  2: "z-[60]",
  3: "z-[70]",
};

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  blocking = false,
  level = 1,
}: DialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(modalRef, open, onClose, blocking);
  if (!open) return null;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center p-4",
        LEVEL_CLASSES[level],
        open ? "pointer-events-auto" : "pointer-events-none",
      )}
      ref={modalRef}
      tabIndex={-1}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className={cn(
          "absolute inset-0 bg-bg-inverse/60 transition-opacity duration-150",
          open ? "opacity-100" : "opacity-0",
        )}
        onClick={blocking ? undefined : onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "relative flex max-h-[calc(100dvh-2rem)] w-full transform flex-col overflow-hidden rounded-xl bg-bg shadow-elevated transition-all duration-150",
          SIZE_CLASSES[size],
          open ? "scale-100 opacity-100" : "scale-95 opacity-0",
        )}
      >
        {/* Header */}
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-base font-semibold text-ink">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-sm text-ink-muted">
                {description}
              </p>
            )}
          </div>
          {!blocking && (
            <button
              type="button"
              onClick={onClose}
              className="focus-ring grid size-8 shrink-0 place-items-center rounded-lg text-ink-muted hover:bg-bg-subtle"
              aria-label={t("common.close")}
            >
              <X className="size-4" />
            </button>
          )}
        </header>

        {/* Body */}
        {children && (
          <div
            tabIndex={0}
            role="region"
            aria-labelledby={titleId}
            className="min-h-0 overflow-y-auto px-5 py-4"
          >
            {children}
          </div>
        )}

        {/* Footer */}
        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line bg-bg-subtle px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
